import dayjs from 'dayjs';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import { dispositionFilename } from '../utils/downloadBlob';
import { unwrapResponse as unwrap, unwrapWithMeta } from '../utils/unwrapResponse';
import { jiraProvisioningOf, type JiraProvisioningMeta } from '../utils/jiraProvisioning';
import type { AvailabilityState, UnavailabilityPeriod } from './unavailabilitiesApi';
import {
  IMPORT_FILE_ERROR_CODES,
  type ExclusionReason,
  type ImportFileError,
  type OpsNote,
  type TechnicianExclusion,
  type TechnicianImportApplyResponse,
  type TechnicianImportValidateResponse,
  type TechnicianProfileFields,
} from './technicianImportContract';

export interface TechnicianProfile {
  firstName: string | null;
  lastName: string | null;
  company: string | null;
  phone: string | null;
  countryCode: string | null;
}

/** One referential value as a badge renders it: the code for the chip, the name for the title. */
export interface ReferentialTag {
  id: number;
  code: string;
  name: string;
}

// Mirrors ADDRESS_KINDS in backend/types/technicians.ts — the three logistics premises (L2c-a).
export const ADDRESS_KINDS = ['billing', 'delivery', 'warehouse'] as const;
export type AddressKind = (typeof ADDRESS_KINDS)[number];
export const MAX_AREA_SQM = 1_000_000;

export interface AddressInput {
  kind: AddressKind;
  line1: string | null;
  postalCode: string | null;
  city: string | null;
  areaSqm: number | null;
  isDefault: boolean;
  placeId: string | null;
}

/** Mirrors `TechnicianAddress.geocodeState` in `backend/types/technicians.ts`. */
export type GeocodeState = 'located' | 'pending' | 'not_found';

export interface TechnicianAddress extends AddressInput {
  geocodeState: GeocodeState;
}

export interface TechnicianActivity {
  interventions: number;
  lastIntervention: string | null;
}

/** Mirrors `ActivityMission` in `backend/types/technicians.ts`. */
export interface ActivityMission {
  requestId: number;
  issueKey: string;
  serviceDeskName: string | null;
  store: string | null;
  requestType: string | null;
  interventionDate: string | null;
  sentAt: string;
}

/** The record's activity: the two numbers plus the missions the card names. */
export type TechnicianRecordActivity = TechnicianActivity & {
  lastMission: ActivityMission | null;
  pendingRequest: ActivityMission | null;
};

export interface Technician {
  id: number;
  username: string;
  email: string;
  lang: string;
  isActive: boolean;
  /** Jira accountId. Null means the mobile account is not provisioned yet. */
  externalId: string | null;
  profile: TechnicianProfile & TechnicianProfileFields;
  /** Primary-only since F18 (PR6): the secondary zone is a separate field below. */
  geoAreaIds: number[];
  /** F18 — a scored fallback zone (weighed like the edge of the distance cap), never a substitute. */
  secondaryGeoAreaIds: number[];
  specialtyIds: number[];
  expertiseIds: number[];
  addresses: TechnicianAddress[];
  ratings: { avg: number | null; count: number };
  activity: TechnicianRecordActivity;
  /** Declared by the technician from his phone; read-only here without exception (D4). */
  unavailabilities: UnavailabilityPeriod[];
  /** Derived by the server from those periods — the screen does not re-derive it. */
  availability: AvailabilityState;
  /** F29 — written by its own routes only, never by the record's save. */
  exclusion: TechnicianExclusion | null;
  /** F33 — same: its own route, so saving the record never touches it. */
  opsNote: OpsNote | null;
  canEdit: boolean;
}

/** One row of the directory. Referential values come whole: displays never filter on `is_active`. */
export interface TechnicianListItem {
  id: number;
  username: string;
  email: string;
  isActive: boolean;
  externalId: string | null;
  profile: TechnicianProfile & { countryName: string | null };
  geoAreas: ReferentialTag[];
  specialties: ReferentialTag[];
  expertises: ReferentialTag[];
  ratings: { avg: number | null; count: number };
  activity: TechnicianActivity;
  /**
   * Derived from the declared periods, never a stored status: an unavailable technician keeps an
   * active account, which is why this and `isActive` are two separate columns. The row carries
   * the word and not the periods — those are on the record, one click away.
   */
  availability: AvailabilityState;
  canEdit: boolean;
}

/** A directory row: F29's exclusion date rides along, independent of `isActive` (M13). */
export interface TechnicianDirectoryRow extends TechnicianListItem {
  excludedAt: string | null;
  /** At least one address in state `not_found`: no distance can be computed from it. */
  unlocated: boolean;
}

// Mirrors TECHNICIAN_ORDER_BY in backend/schemas/technicians.schema.ts — a key added on one side
// only would be silently dropped here or 400 there. `csv.test.ts` pins the two lists equal.
export const TECHNICIAN_ORDER_BY = ['name', 'company', 'area', 'rating', 'interventions', 'lastIntervention'] as const;
export type TechnicianOrderBy = (typeof TECHNICIAN_ORDER_BY)[number];

export const TECHNICIAN_STATUS_FILTERS = ['active', 'inactive', 'excluded'] as const;
export type TechnicianStatusFilter = (typeof TECHNICIAN_STATUS_FILTERS)[number];

export const TECHNICIAN_LOCATION_FILTERS = ['unlocated'] as const;
export type TechnicianLocationFilter = (typeof TECHNICIAN_LOCATION_FILTERS)[number];

export interface TechnicianFilters {
  search?: string;
  countryCode?: string[];
  geoAreaId?: number[];
  specialtyId?: number[];
  expertiseId?: number[];
  /** Independent criteria (M13): an excluded, inactive technician matches both. */
  status?: TechnicianStatusFilter;
  /** Derived from the declared absences — a different question from `status`. */
  availability?: AvailabilityState;
  location?: TechnicianLocationFilter;
  orderBy: TechnicianOrderBy;
  order: 'asc' | 'desc';
  page: number;
  pageSize: number;
}

/**
 * Counters of the whole visible pool, filters aside. `availableToday` and `unavailableToday` count
 * active accounts only and add up to `active`, so the card can state both in one breath.
 */
export interface TechnicianDirectoryStats {
  total: number;
  active: number;
  availableToday: number;
  unavailableToday: number;
  areasCovered: number;
  ratingAvg: number | null;
}

export interface TechnicianDirectoryPage {
  technicians: TechnicianDirectoryRow[];
  total: number;
  page: number;
  pageSize: number;
  stats: TechnicianDirectoryStats;
}

/** What the record form posts. `company` and `phone` are required by the API, not by the table. */
export interface TechnicianInput extends Partial<TechnicianProfileFields> {
  username: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  company: string;
  phone: string;
  countryCode?: string | null;
  lang?: string;
  geoAreaIds: number[];
  /** F18 — absent = no change, `[]` = clear; overlapping the other zone in the same body is a 400. */
  secondaryGeoAreaIds?: number[];
  specialtyIds: number[];
  expertiseIds: number[];
  /** Absent = untouched; present = the full set, a kind left out is deleted (AC18). */
  addresses?: AddressInput[];
}

export interface CreateTechnicianInput extends TechnicianInput {
  password: string;
  /**
   * The account rights, optional on the wire: omitted, the API applies the technician invariants.
   * The form sends them explicitly, since it shows them.
   */
  serviceDeskId?: Array<number | '*'>;
  allowedTicketCreationDesks?: number[];
  permissions?: string[];
}

export interface UpdateTechnicianInput extends TechnicianInput {
  password?: string;
  isActive?: boolean;
}

const BASE = 'admin/technicians';
const jsonHeaders = { 'Content-Type': 'application/json' };

/**
 * Comma-separated lists, the shape the backend schema parses. An empty list is left out.
 * Exported because the screen keeps the view in the URL: one serializer, so the query string in
 * the address bar and the one sent to the API cannot drift apart.
 */
export const directoryQuery = (filters: TechnicianFilters): string => {
  const params = new URLSearchParams({
    orderBy: filters.orderBy,
    order: filters.order,
    page: String(filters.page),
    pageSize: String(filters.pageSize),
  });
  if (filters.search) params.set('search', filters.search);
  if (filters.status) params.set('status', filters.status);
  if (filters.availability) params.set('availability', filters.availability);
  if (filters.location) params.set('location', filters.location);

  const lists: Array<[string, Array<string | number> | undefined]> = [
    ['countryCode', filters.countryCode],
    ['geoAreaId', filters.geoAreaId],
    ['specialtyId', filters.specialtyId],
    ['expertiseId', filters.expertiseId],
  ];
  for (const [key, values] of lists) {
    if (values?.length) params.set(key, values.join(','));
  }
  return params.toString();
};

/**
 * Whether the view is narrowed. Lives here beside the serializer for the same reason: the screen
 * picks its empty message from it and the filter bar shows "Réinitialiser" from it, and the two
 * must agree.
 */
export const hasActiveFilters = (filters: TechnicianFilters): boolean =>
  !!(
    filters.search ||
    filters.countryCode?.length ||
    filters.geoAreaId?.length ||
    filters.specialtyId?.length ||
    filters.expertiseId?.length ||
    filters.status ||
    filters.availability ||
    filters.location
  );

export const fetchTechnicians = async (filters: TechnicianFilters): Promise<TechnicianDirectoryPage> =>
  unwrap<TechnicianDirectoryPage>(
    await fetchWithAuth(`${BASE}?${directoryQuery(filters)}`),
    'Failed to fetch the technicians'
  );

export const fetchTechnician = async (id: number): Promise<Technician> =>
  unwrap<Technician>(await fetchWithAuth(`${BASE}/${id}`), 'Failed to fetch the technician');

/** The record, plus what Jira did about its account. */
export interface CreatedTechnician {
  technician: Technician;
  jira: JiraProvisioningMeta | null;
}

export const createTechnician = async (input: CreateTechnicianInput): Promise<CreatedTechnician> => {
  const { data, meta } = await unwrapWithMeta<Technician>(
    await fetchWithAuth(BASE, { method: 'POST', headers: jsonHeaders, body: JSON.stringify(input) }),
    'Failed to create the technician'
  );
  return { technician: data, jira: jiraProvisioningOf(meta) };
};

export const updateTechnician = async (id: number, input: UpdateTechnicianInput): Promise<Technician> =>
  unwrap<Technician>(
    await fetchWithAuth(`${BASE}/${id}`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify(input) }),
    'Failed to update the technician'
  );

// F29/F33 — each answers the full record, which replaces the cached one.
export const excludeTechnician = async (id: number, reason: ExclusionReason): Promise<Technician> =>
  unwrap<Technician>(
    await fetchWithAuth(`${BASE}/${id}/exclusion`, { method: 'POST', headers: jsonHeaders, body: JSON.stringify({ reason }) }),
    'Failed to exclude the technician'
  );

export const reinstateTechnician = async (id: number): Promise<Technician> =>
  unwrap<Technician>(
    await fetchWithAuth(`${BASE}/${id}/exclusion`, { method: 'DELETE' }),
    'Failed to reinstate the technician'
  );

export const saveOpsNote = async (id: number, text: string): Promise<Technician> =>
  unwrap<Technician>(
    await fetchWithAuth(`${BASE}/${id}/ops-note`, { method: 'PUT', headers: jsonHeaders, body: JSON.stringify({ text }) }),
    'Failed to save the comment'
  );

/**
 * F9 — the .xlsx template (`prefill=false`) or the re-importable base (`prefill=true`), named by
 * the server's Content-Disposition; the M7 name stands in when the header is not readable (CORS).
 */
export const downloadTechnicianWorkbook = async (
  country: string,
  prefill: boolean,
  lang: string
): Promise<{ blob: Blob; filename: string }> => {
  const query = new URLSearchParams({ country, prefill: prefill ? '1' : '0', lang });
  const response = await fetchWithAuth(`${BASE}/import/template?${query}`);
  if (!response.ok) await unwrap(response, 'Failed to prepare the file');
  const fallback = prefill
    ? `techniciens-${country}-base-${dayjs().format('YYYY-MM-DD')}.xlsx`
    : `techniciens-${country}-modele.xlsx`;
  const disposition = response.headers.get('Content-Disposition') ?? '';
  return { blob: await response.blob(), filename: dispositionFilename(disposition) ?? fallback };
};

/** F9 — every answer the import screen acts on; anything else throws (network, 5xx). */
export type TechnicianImportAnswer<T> =
  | { kind: 'ok'; data: T }
  | { kind: 'fileError'; error: ImportFileError }
  | { kind: 'conflict'; preview: TechnicianImportValidateResponse }
  | { kind: 'sessionExpired' };

const count = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const listOf = <T>(value: unknown): T[] => (Array.isArray(value) ? value : []);

/** A partial or malformed payload degrades to empty lists and zero counts, never to a crash. */
const safePreview = (raw: any): TechnicianImportValidateResponse => ({
  fingerprint: typeof raw?.fingerprint === 'string' ? raw.fingerprint : '',
  rowsRead: count(raw?.rowsRead),
  emptyRows: count(raw?.emptyRows),
  // Older payloads carry neither: an unknown answer claims no check was made
  geocodingChecked: raw?.geocodingChecked === true,
  counts: {
    errors: count(raw?.counts?.errors),
    warnings: count(raw?.counts?.warnings),
    additions: count(raw?.counts?.additions),
    changes: count(raw?.counts?.changes),
    toExclude: count(raw?.counts?.toExclude),
    toReinstate: count(raw?.counts?.toReinstate),
    notCreated: count(raw?.counts?.notCreated),
    unchanged: count(raw?.counts?.unchanged),
    absent: count(raw?.counts?.absent),
  },
  errors: listOf<any>(raw?.errors).map((error) => ({
    ...error,
    severity: error?.severity === 'warning' ? 'warning' : 'error',
    cols: Array.isArray(error?.cols) ? error.cols : error?.col ? [error.col] : [],
  })),
  additions: listOf<any>(raw?.additions).map((addition) => ({ ...addition, zonesPrimary: listOf(addition?.zonesPrimary) })),
  changes: listOf<any>(raw?.changes).map((change) => ({ ...change, fields: listOf(change?.fields) })),
  exclusions: {
    toExclude: listOf(raw?.exclusions?.toExclude),
    toReinstate: listOf(raw?.exclusions?.toReinstate),
    notCreated: listOf(raw?.exclusions?.notCreated),
  },
  unchanged: count(raw?.unchanged),
  absent: listOf(raw?.absent),
});

const safeReport = (raw: any): TechnicianImportApplyResponse => ({
  importedAt: typeof raw?.importedAt === 'string' ? raw.importedAt : '',
  counts: {
    created: count(raw?.counts?.created),
    updated: count(raw?.counts?.updated),
    excluded: count(raw?.counts?.excluded),
    reinstated: count(raw?.counts?.reinstated),
    failed: count(raw?.counts?.failed),
  },
  failed: listOf(raw?.failed),
});

const importAnswer = async <T>(
  response: Response,
  fallback: string,
  normalise: (raw: unknown) => T
): Promise<TechnicianImportAnswer<T>> => {
  if (response.status === 401) return { kind: 'sessionExpired' };
  // File errors and the 409 travel raw, outside the `{ success, data }` envelope (PLAN §API).
  if (response.status === 400 || response.status === 409) {
    const body = await response.clone().json().catch(() => null);
    if (response.status === 409 && body?.code === 'preview_outdated' && body.preview) {
      return { kind: 'conflict', preview: safePreview(body.preview) };
    }
    if (IMPORT_FILE_ERROR_CODES.includes(body?.code)) {
      return { kind: 'fileError', error: { code: body.code, column: body.column ?? undefined } };
    }
  }
  return { kind: 'ok', data: normalise(await unwrap<unknown>(response, fallback)) };
};

// No Content-Type header: the browser sets the multipart boundary itself.
const importForm = (fields: Record<string, string | File>): FormData => {
  const form = new FormData();
  Object.entries(fields).forEach(([key, value]) => form.append(key, value));
  return form;
};

export const validateTechnicianImport = async (file: File, country: string, signal?: AbortSignal) =>
  importAnswer<TechnicianImportValidateResponse>(
    await fetchWithAuth(`${BASE}/import/validate`, { method: 'POST', body: importForm({ file, country }), signal }),
    'Failed to analyse the file',
    safePreview
  );

export const applyTechnicianImport = async ({ file, country, fingerprint }: { file: File; country: string; fingerprint: string }) =>
  importAnswer<TechnicianImportApplyResponse>(
    await fetchWithAuth(`${BASE}/import/apply`, { method: 'POST', body: importForm({ file, country, fingerprint }) }),
    'Failed to import the file',
    safeReport
  );

/**
 * What a bulk write really wrote: the ids the server flipped, the ids it refused, and — for the
 * country action — the areas its cascade dropped. Never a blanket success.
 */
export interface BulkOutcome {
  updated: number[];
  skipped: number[];
  clearedAreas: number;
}

/**
 * Status in bulk. A `users` route, guarded on `dashboard:users:manage` and not on the technicians
 * permission — which is why the screen hides these two buttons alone rather than the whole bar.
 * It flips `is_active` and nothing else: a full `PUT` rebuilt from a table row would send back a
 * record nobody typed.
 */
export const bulkSetActive = async (
  action: 'deactivate' | 'reactivate',
  ids: number[]
): Promise<BulkOutcome> => {
  const data = await unwrap<{ deactivatedIds?: number[]; reactivatedIds?: number[] }>(
    await fetchWithAuth(`users/bulk-${action}`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ ids }),
    }),
    'Failed to update the accounts'
  );
  const updated = data?.deactivatedIds ?? data?.reactivatedIds ?? [];
  return { updated, skipped: ids.filter((id) => !updated.includes(id)), clearedAreas: 0 };
};

/**
 * One country for many technicians, with the same cascade the record applies one at a time: the
 * areas of every other country are emptied. The counts reported are the server's, not the ones
 * the table guessed before the write.
 */
export const bulkAssignCountry = async (ids: number[], countryCode: string): Promise<BulkOutcome> => {
  const data = await unwrap<Partial<BulkOutcome>>(
    await fetchWithAuth(`${BASE}/bulk-country`, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ ids, countryCode }),
    }),
    'Failed to assign the country'
  );
  const updated = data?.updated ?? [];
  return {
    updated,
    skipped: data?.skipped ?? ids.filter((id) => !updated.includes(id)),
    clearedAreas: data?.clearedAreas ?? 0,
  };
};

/**
 * What `GET /technicians/me/profile` answers: the directory's own row, for oneself.
 *
 * `canEdit` is absent and that is the whole point — the technician reads these fields, the back
 * office writes them (D11). There is no companion write call here, and there is no route to add
 * one to.
 */
export type MyTechnicianProfile = Omit<TechnicianListItem, 'canEdit'>;

export const fetchMyTechnicianProfile = async (): Promise<MyTechnicianProfile> =>
  unwrap<MyTechnicianProfile>(
    await fetchWithAuth('technicians/me/profile'),
    'Failed to load your information'
  );

